import axios from 'axios';
import { BACKEND_URL } from './config';
import { clearSession, getToken } from './auth';

const client = axios.create({ baseURL: `${BACKEND_URL}/api` });

client.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// s159: trial / membership over → the brand home shows the pay modal
// (lmsbeta.aurexnetwork.com → beta.aurexnetwork.com, lms.carlosartiles.com → carlosartiles.com)
const brandHome = () => {
  const parts = window.location.hostname.split('.');
  const first = parts.shift();
  return `${window.location.protocol}//${/beta$/.test(first) ? 'beta.' : ''}${parts.join('.')}/?membership=expired`;
};
const membershipLapsed = (err) => err.response?.status === 403 && err.response?.data?.detail?.code === 'membership_lapsed';

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (membershipLapsed(err)) { window.location.assign(brandHome()); return Promise.reject(err); }
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      clearSession();
      window.location.assign('/login');
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (username, password) => client.post('/auth/login', { username, password }),
  ssoLogin: (token) => client.get('/auth/sso-login', { params: { token } }),
  me: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout'),
};

export const healthAPI = {
  check: () => client.get('/health'),
};

export const brandAPI = {
  get: () => client.get('/public/brand'),
};

export const rulesAPI = {
  list: () => client.get('/admin/rules'),
  create: (data) => client.post('/admin/rules', data),
  update: (id, data) => client.put(`/admin/rules/${id}`, data),
  remove: (id) => client.delete(`/admin/rules/${id}`),
};

export const ledgerAPI = {
  list: (params = {}) => client.get('/admin/ledger', { params }),
  action: (id, action, note = '') => client.post(`/admin/ledger/${id}/action`, { action, note }),
  wallet: (mid) => client.get(`/admin/wallet/${mid}`),
  myWallet: () => client.get('/me/wallet'),
};

export const eventsAPI = {
  list: (params = {}) => client.get('/admin/events', { params }),
};

// MMS_POINTS_HUB_PLAN F1/F2 — points by product + the My Account Reports port.
export const reportsAPI = {
  points: (params = {}) => client.get('/admin/points/overview', { params }),
  overview: (params = {}) => client.get('/admin/reports/overview', { params }),
};

export const affiliatesAPI = {
  dashboard: (days = 30) => client.get('/admin/dashboard', { params: { days } }),
  list: (params = {}) => client.get('/admin/affiliates', { params }),
  update: (mid, data) => client.put(`/admin/affiliates/${mid}`, data),
  referrals: (params = {}) => client.get('/admin/referrals', { params }),
  payouts: (params = {}) => client.get('/admin/payouts', { params }),
  resolvePayout: (id, data) => client.put(`/admin/payouts/${id}`, data),
  flags: (params = {}) => client.get('/admin/flags', { params }),
  resolveFlag: (id) => client.put(`/admin/flags/${id}`),
  program: () => client.get('/admin/program'),
  putProgram: (data) => client.put('/admin/program', data),
  referralAction: (id, action, note = '') => client.put(`/admin/referrals/${id}`, { action, note }),
  groups: () => client.get('/admin/groups'),
  createGroup: (data) => client.post('/admin/groups', data),
  deleteGroup: (id) => client.delete(`/admin/groups/${id}`),
  pages: () => client.get('/admin/pages'),
  putPages: (data) => client.put('/admin/pages', data),
  runExpiry: () => client.post('/admin/run-expiry'),
  publicPage: (key) => client.get(`/public/pages/${key}`),
  uploadTaxDoc: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/me/tax-document', fd);
  },
  plans: () => client.get('/admin/plans'),
  createPlan: (data) => client.post('/admin/plans', data),
  updatePlan: (id, data) => client.put(`/admin/plans/${id}`, data),
  deletePlan: (id) => client.delete(`/admin/plans/${id}`),
};

export const rewardsAPI = {
  me: () => client.get('/me/rewards'),
  catalog: () => client.get('/catalog'),
  redeem: (itemId) => client.post(`/me/redeem/${itemId}`),
  myRedemptions: () => client.get('/me/redemptions'),
  adminCatalog: () => client.get('/admin/catalog'),
  createItem: (data) => client.post('/admin/catalog', data),
  updateItem: (id, data) => client.put(`/admin/catalog/${id}`, data),
  deleteItem: (id) => client.delete(`/admin/catalog/${id}`),
  adminRedemptions: (params = {}) => client.get('/admin/redemptions', { params }),
  resolveRedemption: (id, action, note = '') => client.put(`/admin/redemptions/${id}`, { action, note }),
  levels: () => client.get('/admin/levels'),
  putLevels: (items) => client.put('/admin/levels', { items }),
};

export const engageAPI = {
  announcements: () => client.get('/announcements'),
  assets: () => client.get('/assets'),
  leaderboard: (metric, period) => client.get('/leaderboard', { params: { metric, period } }),
  contests: () => client.get('/contests'),
  myBonuses: () => client.get('/me/bonuses'),
  publicLeaderboard: () => client.get('/public/leaderboard'),
  adminAnnouncements: () => client.get('/admin/announcements'),
  createAnnouncement: (data) => client.post('/admin/announcements', data),
  updateAnnouncement: (id, data) => client.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => client.delete(`/admin/announcements/${id}`),
  adminAssets: () => client.get('/admin/assets'),
  uploadAsset: (file, title) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/admin/assets?title=${encodeURIComponent(title || '')}`, fd);
  },
  deleteAsset: (id) => client.delete(`/admin/assets/${id}`),
  adminContests: () => client.get('/admin/contests'),
  createContest: (data) => client.post('/admin/contests', data),
  finishContest: (id) => client.post(`/admin/contests/${id}/finish`),
  deleteContest: (id) => client.delete(`/admin/contests/${id}`),
  standings: (id) => client.get(`/admin/contests/${id}/standings`),
  milestones: () => client.get('/admin/milestones'),
  createMilestone: (data) => client.post('/admin/milestones', data),
  deleteMilestone: (id) => client.delete(`/admin/milestones/${id}`),
  bonuses: () => client.get('/admin/bonuses'),
  createBonus: (data) => client.post('/admin/bonuses', data),
  deleteBonus: (id) => client.delete(`/admin/bonuses/${id}`),
  lbConfig: () => client.get('/admin/leaderboard-config'),
  putLbConfig: (data) => client.put('/admin/leaderboard-config', data),
};

export const messagesAPI = {
  myThread: () => client.get('/me/thread'),
  send: (body) => client.post('/me/thread/messages', { body }),
  notifications: () => client.get('/me/notifications'),
  markRead: () => client.post('/me/notifications/read'),
  adminThreads: (params = {}) => client.get('/admin/threads', { params }),
  adminOpen: (tid) => client.get(`/admin/threads/${tid}/messages`),
  adminSend: (tid, body) => client.post(`/admin/threads/${tid}/messages`, { body }),
};

export const portalAPI = {
  me: () => client.get('/me/affiliate'),
  links: () => client.get('/me/links'),
  createLink: (data) => client.post('/me/links', data),
  deleteLink: (code) => client.delete(`/me/links/${code}`),
  setPayoutMethod: (data) => client.put('/me/payout-method', data),
  withdrawals: () => client.get('/me/withdrawals'),
  requestWithdrawal: (amount) => client.post('/me/withdrawals', { amount }),
};

export default client;

// s149 — KMS reader coupons (single-use, amount or %, one plan)
export const couponsAPI = {
  list: (params) => client.get('/admin/coupons', { params }),
  create: (body) => client.post('/admin/coupons', body),
  release: (id) => client.post(`/admin/coupons/${id}/release`),
  remove: (id) => client.delete(`/admin/coupons/${id}`),
};
